import { useCallback, useMemo } from 'react';
import { createSearchParams, useNavigate } from 'react-router';

import SmallCard from '@/components/SmallCard';

import { calcChangePercentage } from '../../Dashboard.utils';
import useStyles from '../DashboardVMP.styles';
import type { VMPSmallCardsProps } from './VmpSmallCards.types';

const VMPSmallCards = (props: VMPSmallCardsProps) => {
  const { vulnerabilities, prevVulnerabilities, assets, timeFilter } = props;
  const navigate = useNavigate();
  const { classes } = useStyles();

  const currentTimeframeVulnerabilities = useMemo(() => {
    return (vulnerabilities?.cvss_level ?? []).reduce(
      (sum: number, group: { count?: number }) => sum + (group.count ?? 0),
      0
    );
  }, [vulnerabilities]);

  const prevTimeframeVulnerabilities = useMemo(() => {
    return (prevVulnerabilities?.cvss_level ?? []).reduce(
      (sum: number, group: { count?: number }) => sum + (group.count ?? 0),
      0
    );
  }, [prevVulnerabilities]);

  const changeInVulnerabilities = useMemo(() => {
    const changePercent = calcChangePercentage(
      currentTimeframeVulnerabilities,
      prevTimeframeVulnerabilities
    );
    return changePercent !== null ? `${changePercent.toFixed(1)}%` : '';
  }, [currentTimeframeVulnerabilities, prevTimeframeVulnerabilities]);

  // Handle card clicks for navigation
  const handleCardClick = useCallback(
    (title: string) => {
      if (title === 'Total Vulnerabilities') {
        navigate({
          pathname: '/vulnerability-management/vulnerabilities',
          search: createSearchParams({
            t: timeFilter,
            f: JSON.stringify({
              date_remediated: ['__isnull', true]
            })
          }).toString()
        });
      } else if (title === 'Total VMPE Assets') {
        const assetsTimeFilter = timeFilter.replace(/C$/, 'U');
        navigate({
          pathname: '/asset-inventory',
          search: createSearchParams({
            t: assetsTimeFilter
          }).toString()
        });
      } else {
        navigate({
          pathname: '/vulnerability-management/vulnerabilities',
          search: createSearchParams({
            t: timeFilter,
            f: JSON.stringify({
              cvss_level: ['__eq', title.split(' ')[0]],
              date_remediated: ['__isnull', true]
            })
          }).toString()
        });
      }
    },
    [timeFilter]
  );

  const criticalVulnerabilities = useMemo(
    () =>
      vulnerabilities?.cvss_level?.filter(
        (level) => level.value === 'Critical'
      ) ?? [],
    [vulnerabilities]
  );

  const highVulnerabilities = useMemo(
    () =>
      vulnerabilities?.cvss_level?.filter((level) => level.value === 'High') ??
      [],
    [vulnerabilities]
  );

  return (
    <>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          color="error"
          title="Critical Vulnerabilities"
          primaryContent={
            criticalVulnerabilities[0]?.count?.toLocaleString() ?? 0
          }
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          color="warning"
          title="High Vulnerabilities"
          primaryContent={highVulnerabilities[0]?.count?.toLocaleString() ?? 0}
          onCardClick={handleCardClick}
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          onCardClick={handleCardClick}
          title="Total Vulnerabilities"
          primaryContent={
            currentTimeframeVulnerabilities?.toLocaleString() ?? 0
          }
          chipContent={changeInVulnerabilities}
        />
      </div>
      <div style={{ cursor: 'pointer' }} className={classes.dashboardSmallItem}>
        <SmallCard
          title="Total VMPE Assets"
          primaryContent={assets.length.toLocaleString()}
          onCardClick={handleCardClick}
        />
      </div>
    </>
  );
};

VMPSmallCards.displayName = 'VMPSmallCards';

export default VMPSmallCards;
